// frontend/src/components/DesempenhoFiltrosModal/DesempenhoFiltrosModal.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Switch, Typography, Space, Alert, Empty } from 'antd';
import { agruparPortadores, normalizarNomePortador } from '../../utils/desempenhoFiltros';

const { Text } = Typography;

const Linha = ({ titulo, subtitulo, checked, onChange }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
            <Text>{titulo}</Text>
            {subtitulo ? (
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }} ellipsis>
                    {subtitulo}
                </Text>
            ) : null}
        </div>
        <Switch checked={checked} onChange={onChange} />
    </div>
);

/**
 * Escolhe o que entra nos indicadores do Meu Desempenho.
 * Switch ligado = aparece; desligado = oculto (guardado em desempenho_filtros).
 */
function DesempenhoFiltrosModal({ open, onClose, onSave, saving, value, cartoes, adicionais, isMobile }) {
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        if (open) setDraft(value);
    }, [open, value]);

    const portadores = useMemo(() => agruparPortadores(adicionais, cartoes), [adicionais, cartoes]);

    const portadorOculto = (chave) =>
        draft.portadores_ocultos.some((n) => normalizarNomePortador(n) === chave);

    const togglePortador = (grupo, visivel) => {
        setDraft((prev) => {
            const resto = prev.portadores_ocultos.filter((n) => normalizarNomePortador(n) !== grupo.chave);
            return { ...prev, portadores_ocultos: visivel ? resto : [...resto, grupo.nome] };
        });
    };

    const toggleCartao = (id, visivel) => {
        setDraft((prev) => {
            const resto = prev.cartoes_ocultos.filter((c) => c !== id);
            return { ...prev, cartoes_ocultos: visivel ? resto : [...resto, id] };
        });
    };

    const limpar = () => setDraft({ cartoes_ocultos: [], portadores_ocultos: [] });

    return (
        <Modal
            title="Filtrar indicadores"
            open={open}
            onCancel={onClose}
            width={isMobile ? '100%' : 520}
            destroyOnClose
            footer={[
                <Button key="limpar" onClick={limpar} disabled={saving}>
                    Mostrar tudo
                </Button>,
                <Button key="cancel" onClick={onClose}>
                    Cancelar
                </Button>,
                <Button key="ok" type="primary" loading={saving} onClick={() => onSave(draft)}>
                    Salvar
                </Button>,
            ]}
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Desligue o que não deve entrar nas suas receitas, despesas, categorias, metas e insights.
                Vale para a web e para o app.
            </Text>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Portadores (cartões adicionais)
            </Text>
            {portadores.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Nenhum cartão adicional cadastrado."
                    style={{ margin: '8px 0 20px' }}
                />
            ) : (
                <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size={10}>
                    {portadores.map((g) => (
                        <Linha
                            key={g.chave}
                            titulo={g.nome}
                            subtitulo={g.cartoes.join(', ')}
                            checked={!portadorOculto(g.chave)}
                            onChange={(visivel) => togglePortador(g, visivel)}
                        />
                    ))}
                </Space>
            )}
            {portadores.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 20 }}
                    message="O saldo atual e o saldo previsto continuam contando a fatura inteira — é o dinheiro que sai da sua conta. Reembolsos gerados para o portador oculto também saem das receitas."
                />
            )}

            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Cartões
            </Text>
            {(cartoes || []).length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum cartão ativo." />
            ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    {cartoes.map((c) => (
                        <Linha
                            key={c.id}
                            titulo={c.nome}
                            checked={!draft.cartoes_ocultos.includes(c.id)}
                            onChange={(visivel) => toggleCartao(c.id, visivel)}
                        />
                    ))}
                </Space>
            )}
        </Modal>
    );
}

export default DesempenhoFiltrosModal;
